import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  filterGuestProfiles,
  normalizeGuestIdNumber,
  normalizeGuestPhone,
} from "../../services/guestProfileService";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const lower = (value) =>
  clean(value).toLowerCase();

const formatDate = (value) => {
  if (!value) return "";

  let date = null;

  if (value?.toDate instanceof Function) {
    date = value.toDate();
  } else if (value?.seconds) {
    date = new Date(value.seconds * 1000);
  } else {
    date = new Date(value);
  }

  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const maskIdNumber = (value) => {
  const idNumber = clean(value);

  if (!idNumber) return "-";

  if (idNumber.length <= 4) {
    return idNumber;
  }

  return `${"•".repeat(
    Math.max(4, idNumber.length - 4)
  )}${idNumber.slice(-4)}`;
};

const getProfileId = (profile) =>
  clean(
    profile?.profileId ||
      profile?.id ||
      profile?.guestProfileId
  );

const getPhoneNumber = (profile) =>
  normalizeGuestPhone(
    profile?.phoneNumber ||
      profile?.phone ||
      ""
  );

const getIdNumber = (profile) =>
  normalizeGuestIdNumber(
    profile?.idNumber || ""
  );

const getLastVisitDate = (profile) =>
  formatDate(
    profile?.lastVisitedAt ||
      profile?.lastVisitAt ||
      profile?.updatedAt ||
      profile?.createdAt
  );

const getVisitCount = (profile) => {
  const value = Number(profile?.visitCount || 0);
  return Number.isFinite(value) ? value : 0;
};

const getIsVip = (profile) =>
  profile?.isVip === true ||
  profile?.isVIP === true ||
  profile?.guestTag === "vip";

const getIsBlacklisted = (profile) =>
  profile?.isBlacklisted === true ||
  profile?.blacklisted === true ||
  profile?.guestTag === "blacklist";

const hasIdDocument = (profile) =>
  Boolean(
    clean(profile?.idFrontUrl) ||
      clean(profile?.idBackUrl)
  );

const escapeRegExp = (value) =>
  String(value).replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&"
  );

/* -------------------------------------------------------------------------- */
/*                              Highlight Text                                */
/* -------------------------------------------------------------------------- */

function HighlightText({
  text,
  search,
  style,
}) {
  const sourceText = clean(text);
  const searchText = clean(search);

  if (!sourceText || !searchText) {
    return <span style={style}>{sourceText}</span>;
  }

  const normalizedSearch =
    normalizeGuestPhone(searchText) ||
    normalizeGuestIdNumber(searchText) ||
    searchText;

  const expression = new RegExp(
    `(${escapeRegExp(normalizedSearch)})`,
    "ig"
  );

  const parts = sourceText.split(expression);

  return (
    <span style={style}>
      {parts.map((part, index) => {
        const isMatch =
          part.toLowerCase() ===
          normalizedSearch.toLowerCase();

        return isMatch ? (
          <mark
            key={`${part}-${index}`}
            style={highlightStyle}
          >
            {part}
          </mark>
        ) : (
          <React.Fragment
            key={`${part}-${index}`}
          >
            {part}
          </React.Fragment>
        );
      })}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                         Guest Search Input Component                        */
/* -------------------------------------------------------------------------- */

function GuestSearchInput({
  profiles = [],
  value = "",
  onChange,
  onSelect,
  disabled = false,

  loading = false,
  hotelId = "",
  maxSuggestions = 8,

  placeholder = "Name, phone ya ID number search karo",
  onNewGuestAction,
  newGuestActionLabel = "⚡ Save Name Only & Occupy Room",
}) {
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] =
    useState(-1);

  const searchValue = String(value ?? "");

  const suggestions = useMemo(() => {
    if (!searchValue.trim()) {
      return [];
    }

    return filterGuestProfiles(
      profiles,
      searchValue,
      {
        limit: maxSuggestions,
        includeBlacklisted: true,
        hotelId,
      }
    );
  }, [
    profiles,
    searchValue,
    maxSuggestions,
    hotelId,
  ]);

  const hasExactMatch = useMemo(() => {
    const raw = clean(searchValue);
    if (!raw) return false;

    const lowerQuery = lower(raw);
    const phoneQuery = normalizeGuestPhone(raw);
    const idQuery = normalizeGuestIdNumber(raw);

    return (profiles || []).some((profile) => {
      const profileName = lower(profile?.name);
      const profilePhone = getPhoneNumber(profile);
      const profileIdNumber = getIdNumber(profile);

      return (
        (profileName && profileName === lowerQuery) ||
        (phoneQuery && profilePhone && profilePhone === phoneQuery) ||
        (idQuery && profileIdNumber && profileIdNumber === idQuery)
      );
    });
  }, [profiles, searchValue]);

  const showNewGuestAction =
    !loading &&
    !disabled &&
    clean(searchValue) &&
    !hasExactMatch &&
    typeof onNewGuestAction === "function";

  const shouldShowMenu =
    open &&
    !disabled &&
    searchValue.trim().length > 0;

  /* ------------------------------------------------------------------------ */
  /*                        Reset Active Suggestion                            */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    setActiveIndex(
      suggestions.length > 0 ? 0 : -1
    );
  }, [suggestions]);

  /* ------------------------------------------------------------------------ */
  /*                        Outside Click Close                                */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(
          event.target
        )
      ) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /*                     Scroll Active Option Into View                        */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (activeIndex < 0) return;

    optionRefs.current[
      activeIndex
    ]?.scrollIntoView({
      block: "nearest",
    });
  }, [activeIndex]);

  /* ------------------------------------------------------------------------ */
  /*                            Select Profile                                 */
  /* ------------------------------------------------------------------------ */

  const selectProfile = (profile) => {
    if (!profile || disabled) return;

    onSelect?.(profile);

    setOpen(false);
    setActiveIndex(-1);
  };

  /* ------------------------------------------------------------------------ */
  /*                          Keyboard Navigation                              */
  /* ------------------------------------------------------------------------ */

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();

      if (!open) {
        setOpen(true);
      }

      if (suggestions.length > 0) {
        setActiveIndex((current) =>
          current < suggestions.length - 1
            ? current + 1
            : 0
        );
      }

      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();

      if (!open) {
        setOpen(true);
      }

      if (suggestions.length > 0) {
        setActiveIndex((current) =>
          current > 0
            ? current - 1
            : suggestions.length - 1
        );
      }

      return;
    }

    if (event.key === "Enter") {
      if (
        open &&
        activeIndex >= 0 &&
        suggestions[activeIndex]
      ) {
        event.preventDefault();

        selectProfile(
          suggestions[activeIndex]
        );
      }

      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                              Input Change                                 */
  /* ------------------------------------------------------------------------ */

  const handleChange = (event) => {
    const nextValue = event.target.value;

    onChange?.(nextValue);

    setOpen(true);
    setActiveIndex(-1);
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      ref={wrapperRef}
      style={wrapperStyle}
    >
      <div style={inputWrapperStyle}>
        <span style={searchIconStyle}>
          🔍
        </span>

        <input
          ref={inputRef}
          value={searchValue}
          disabled={disabled}
          onFocus={() => {
            setOpen(true);
          }}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{
            ...inputStyle,
            paddingRight:
              searchValue && !disabled
                ? 42
                : 12,
            background: disabled
              ? "#f1f5f9"
              : "#ffffff",
            cursor: disabled
              ? "not-allowed"
              : "text",
          }}
          autoComplete="off"
          spellCheck={false}
          aria-label="Search existing guest"
          aria-expanded={shouldShowMenu}
          aria-haspopup="listbox"
        />

        {searchValue && !disabled && (
          <button
            type="button"
            onClick={() => {
              onChange?.("");
              setOpen(false);
              setActiveIndex(-1);

              setTimeout(() => {
                inputRef.current?.focus();
              }, 0);
            }}
            style={clearButtonStyle}
            title="Search clear karo"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {shouldShowMenu && (
        <div
          style={menuStyle}
          role="listbox"
        >
          {loading ? (
            <div style={messageStyle}>
              <div style={spinnerStyle} />

              <span>
                Guest profiles loading...
              </span>
            </div>
          ) : suggestions.length === 0 ? (
            <>
              <div style={newGuestBoxStyle}>
                <div style={newGuestIconStyle}>
                  ＋
                </div>

                <div>
                  <strong
                    style={{
                      display: "block",
                      color: "#0f172a",
                    }}
                  >
                    Naya guest profile banega
                  </strong>

                  <small style={mutedTextStyle}>
                    “{searchValue.trim()}” se koi
                    exact existing guest nahi mila.
                  </small>
                </div>
              </div>

              {showNewGuestAction && (
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    setOpen(false);
                    setActiveIndex(-1);
                    onNewGuestAction?.(clean(searchValue));
                  }}
                  style={newGuestActionStyle}
                >
                  {newGuestActionLabel}
                </button>
              )}
            </>
          ) : (
            <>
              <div style={menuHeaderStyle}>
                <span>
                  Existing Guests
                </span>

                <span>
                  {suggestions.length} result
                  {suggestions.length > 1
                    ? "s"
                    : ""}
                </span>
              </div>

              {suggestions.map(
                (profile, index) => {
                  const profileId =
                    getProfileId(profile);

                  const phoneNumber =
                    getPhoneNumber(profile);

                  const idNumber =
                    getIdNumber(profile);

                  const isVip =
                    getIsVip(profile);

                  const isBlacklisted =
                    getIsBlacklisted(
                      profile
                    );

                  const visitCount =
                    getVisitCount(profile);

                  const lastVisitDate =
                    getLastVisitDate(
                      profile
                    );

                  const idUploaded =
                    hasIdDocument(profile);

                  const isActive =
                    index === activeIndex;

                  return (
                    <button
                      ref={(element) => {
                        optionRefs.current[
                          index
                        ] = element;
                      }}
                      key={
                        profileId ||
                        `${phoneNumber}-${idNumber}-${index}`
                      }
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      style={{
                        ...optionStyle,
                        background: isActive
                          ? "#eff6ff"
                          : "#ffffff",
                        borderLeft: isActive
                          ? "4px solid #2563eb"
                          : "4px solid transparent",
                      }}
                      onMouseEnter={() =>
                        setActiveIndex(index)
                      }
                      onMouseDown={(event) =>
                        event.preventDefault()
                      }
                      onClick={() =>
                        selectProfile(profile)
                      }
                    >
                      <div
                        style={
                          optionTopRowStyle
                        }
                      >
                        <div
                          style={
                            guestIdentityStyle
                          }
                        >
                          <div
                            style={
                              avatarStyle
                            }
                          >
                            {clean(
                              profile?.name
                            )
                              .charAt(0)
                              .toUpperCase() ||
                              "G"}
                          </div>

                          <div
                            style={{
                              minWidth: 0,
                            }}
                          >
                            <div
                              style={
                                guestNameRowStyle
                              }
                            >
                              <HighlightText
                                text={
                                  profile?.name ||
                                  "Guest"
                                }
                                search={
                                  searchValue
                                }
                                style={
                                  guestNameStyle
                                }
                              />

                              {isVip && (
                                <span
                                  style={
                                    vipBadgeStyle
                                  }
                                >
                                  ⭐ VIP
                                </span>
                              )}

                              {isBlacklisted && (
                                <span
                                  style={
                                    blacklistBadgeStyle
                                  }
                                >
                                  🚫 Blacklist
                                </span>
                              )}
                            </div>

                            <div
                              style={
                                phoneStyle
                              }
                            >
                              📞{" "}
                              <HighlightText
                                text={
                                  phoneNumber ||
                                  "No phone"
                                }
                                search={
                                  searchValue
                                }
                              />
                            </div>
                          </div>
                        </div>

                        <div
                          style={
                            selectBadgeStyle
                          }
                        >
                          Select
                        </div>
                      </div>

                      <div
                        style={
                          detailsGridStyle
                        }
                      >
                        <div
                          style={
                            detailItemStyle
                          }
                        >
                          <span
                            style={
                              detailLabelStyle
                            }
                          >
                            ID
                          </span>

                          <strong
                            style={
                              detailValueStyle
                            }
                          >
                            {clean(
                              profile?.idType
                            ) || "ID"}
                            :{" "}
                            {maskIdNumber(
                              idNumber
                            )}
                          </strong>
                        </div>

                        <div
                          style={
                            detailItemStyle
                          }
                        >
                          <span
                            style={
                              detailLabelStyle
                            }
                          >
                            Visits
                          </span>

                          <strong
                            style={
                              detailValueStyle
                            }
                          >
                            {visitCount}
                          </strong>
                        </div>

                        <div
                          style={
                            detailItemStyle
                          }
                        >
                          <span
                            style={
                              detailLabelStyle
                            }
                          >
                            Last Visit
                          </span>

                          <strong
                            style={
                              detailValueStyle
                            }
                          >
                            {lastVisitDate ||
                              "-"}
                          </strong>
                        </div>

                        <div
                          style={
                            detailItemStyle
                          }
                        >
                          <span
                            style={
                              detailLabelStyle
                            }
                          >
                            ID Photo
                          </span>

                          <strong
                            style={{
                              ...detailValueStyle,
                              color: idUploaded
                                ? "#15803d"
                                : "#dc2626",
                            }}
                          >
                            {idUploaded
                              ? "Uploaded ✅"
                              : "Missing ❌"}
                          </strong>
                        </div>
                      </div>

                      {isBlacklisted &&
                        clean(
                          profile?.blacklistReason
                        ) && (
                          <div
                            style={
                              blacklistReasonStyle
                            }
                          >
                            Reason:{" "}
                            {clean(
                              profile.blacklistReason
                            )}
                          </div>
                        )}
                    </button>
                  );
                }
              )}

              {showNewGuestAction && (
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    setOpen(false);
                    setActiveIndex(-1);
                    onNewGuestAction?.(clean(searchValue));
                  }}
                  style={newGuestActionStyle}
                >
                  {newGuestActionLabel}
                </button>
              )}

              <div style={menuFooterStyle}>
                ↑ ↓ navigate • Enter select •
                Esc close
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const wrapperStyle = {
  position: "relative",
  width: "100%",
};

const inputWrapperStyle = {
  position: "relative",
  width: "100%",
};

const searchIconStyle = {
  position: "absolute",
  left: 12,
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1,
  fontSize: 14,
  pointerEvents: "none",
};

const inputStyle = {
  width: "100%",
  minHeight: 44,
  padding: "11px 12px 11px 38px",
  border: "1px solid #94a3b8",
  borderRadius: 11,
  boxSizing: "border-box",
  fontSize: 14,
  color: "#0f172a",
  outline: "none",
};

const clearButtonStyle = {
  position: "absolute",
  right: 7,
  top: "50%",
  transform: "translateY(-50%)",
  width: 30,
  height: 30,
  border: 0,
  borderRadius: 8,
  background: "#e2e8f0",
  color: "#475569",
  fontSize: 20,
  lineHeight: 1,
  cursor: "pointer",
};

const menuStyle = {
  position: "absolute",
  zIndex: 1000,
  top: "calc(100% + 7px)",
  left: 0,
  right: 0,
  maxHeight: 430,
  overflowY: "auto",
  background: "#ffffff",
  border: "1px solid #cbd5e1",
  borderRadius: 14,
  boxShadow:
    "0 20px 55px rgba(15,23,42,.22)",
};

const menuHeaderStyle = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  padding: "10px 12px",
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#475569",
  fontSize: 12,
  fontWeight: 800,
};

const optionStyle = {
  width: "100%",
  display: "block",
  padding: 12,
  borderTop: 0,
  borderRight: 0,
  borderBottom: "1px solid #e2e8f0",
  cursor: "pointer",
  textAlign: "left",
  transition:
    "background-color .12s ease",
};

const optionTopRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 10,
};

const guestIdentityStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  minWidth: 0,
};

const avatarStyle = {
  flex: "0 0 auto",
  width: 39,
  height: 39,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#dbeafe",
  color: "#1d4ed8",
  fontSize: 17,
  fontWeight: 900,
};

const guestNameRowStyle = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 6,
};

const guestNameStyle = {
  color: "#0f172a",
  fontSize: 14,
  fontWeight: 900,
  overflowWrap: "anywhere",
};

const phoneStyle = {
  marginTop: 3,
  color: "#475569",
  fontSize: 12,
};

const vipBadgeStyle = {
  padding: "3px 7px",
  borderRadius: 999,
  background: "#fef3c7",
  color: "#92400e",
  fontSize: 10,
  fontWeight: 900,
};

const blacklistBadgeStyle = {
  padding: "3px 7px",
  borderRadius: 999,
  background: "#fee2e2",
  color: "#b91c1c",
  fontSize: 10,
  fontWeight: 900,
};

const selectBadgeStyle = {
  flex: "0 0 auto",
  padding: "5px 8px",
  borderRadius: 8,
  background: "#dbeafe",
  color: "#1d4ed8",
  fontSize: 10,
  fontWeight: 900,
};

const detailsGridStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(105px,1fr))",
  gap: 7,
  marginTop: 10,
};

const detailItemStyle = {
  minWidth: 0,
  padding: "7px 8px",
  borderRadius: 8,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
};

const detailLabelStyle = {
  display: "block",
  marginBottom: 3,
  color: "#64748b",
  fontSize: 9,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: ".4px",
};

const detailValueStyle = {
  display: "block",
  color: "#334155",
  fontSize: 11,
  fontWeight: 800,
  overflowWrap: "anywhere",
};

const blacklistReasonStyle = {
  marginTop: 9,
  padding: "7px 9px",
  borderRadius: 8,
  background: "#fef2f2",
  color: "#b91c1c",
  fontSize: 11,
  fontWeight: 700,
};

const messageStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 9,
  padding: 18,
  color: "#64748b",
  fontSize: 13,
};

const spinnerStyle = {
  width: 18,
  height: 18,
  border: "2px solid #cbd5e1",
  borderTopColor: "#2563eb",
  borderRadius: "50%",
};

const newGuestBoxStyle = {
  display: "flex",
  alignItems: "center",
  gap: 11,
  padding: 15,
  background: "#f8fafc",
};

const newGuestActionStyle = {
  width: "calc(100% - 20px)",
  margin: "10px",
  padding: "11px 12px",
  border: 0,
  borderRadius: 10,
  background: "#d97706",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
  textAlign: "center",
};

const newGuestIconStyle = {
  width: 38,
  height: 38,
  flex: "0 0 auto",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#dcfce7",
  color: "#15803d",
  fontSize: 22,
  fontWeight: 900,
};

const mutedTextStyle = {
  display: "block",
  marginTop: 4,
  color: "#64748b",
  fontSize: 11,
};

const menuFooterStyle = {
  position: "sticky",
  bottom: 0,
  padding: "8px 11px",
  borderTop: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#64748b",
  fontSize: 10,
  textAlign: "center",
};

const highlightStyle = {
  padding: 0,
  background: "#fef08a",
  color: "inherit",
  borderRadius: 2,
};

export default GuestSearchInput;